"use client";

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { brokerCredentialsApi, type BrokerCredentialDto, type BrokerId } from "@/lib/api";
import { useTenant } from "@/hooks/useTenant";

const THIRTY_SECONDS = 30 * 1000;

/**
 * A supported broker joined with the active org's stored credential (if any).
 * When `credential` is null the org hasn't supplied BYO broker creds yet.
 */
export interface BrokerCredentialRow {
  broker: { id: BrokerId; displayName: string; fields: BrokerField[] };
  credential: BrokerCredentialDto | null;
}

export interface BrokerField {
  key: string;
  label: string;
  secret: boolean;
}

/**
 * SFWL-006 Phase 2: supported brokers + their credential field schema. IBKR is
 * deferred per the Sweet customer spec, so CQG is the only entry today.
 */
export const SUPPORTED_BROKERS: BrokerCredentialRow["broker"][] = [
  {
    id: "cqg",
    displayName: "CQG",
    fields: [
      { key: "username", label: "Username", secret: false },
      { key: "password", label: "Password", secret: true },
      { key: "clientAppId", label: "Client App ID", secret: false },
    ],
  },
];

/**
 * SFWL-006 Phase 2: BYO broker credential management for the active org.
 *
 * Backs the FinancialsSettings UI: lists the org's masked credentials
 * (`GET /broker-credentials`, org-admin scoped), joins them with the supported
 * broker list, and exposes save / validate ("Test connection") / delete
 * mutations. Query key includes the tenant id so switching subdomains flushes
 * the previous org's view.
 */
export function useBrokerCredentials() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  const tenantId = tenant.id;
  const scopeKey = tenantId ?? "default";

  const query = useQuery({
    queryKey: ["broker-credentials", scopeKey],
    // x-tenant-id is attached globally by fetchWithTimeout (registered via
    // TenantThemeProvider); the tenant id here only scopes the cache key.
    queryFn: () => brokerCredentialsApi.list(),
    staleTime: THIRTY_SECONDS,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["broker-credentials", scopeKey] });

  const upsertMutation = useMutation({
    mutationFn: ({
      broker,
      payload,
    }: {
      broker: BrokerId;
      payload: Record<string, unknown>;
    }) => brokerCredentialsApi.upsert(broker, payload),
    onSuccess: invalidate,
  });

  const validateMutation = useMutation({
    mutationFn: (id: string) => brokerCredentialsApi.validate(id),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => brokerCredentialsApi.remove(id),
    onSuccess: invalidate,
  });

  const rows = useMemo<BrokerCredentialRow[]>(() => {
    const credentials = query.data?.credentials ?? [];
    const byBroker = new Map(credentials.map((c) => [c.broker, c]));
    return SUPPORTED_BROKERS.map((broker) => ({
      broker,
      credential: byBroker.get(broker.id) ?? null,
    }));
  }, [query.data]);

  return {
    rows,
    isLoading: query.isPending,
    error: query.error as Error | null,
    saveCredential: upsertMutation.mutateAsync,
    validateCredential: validateMutation.mutateAsync,
    deleteCredential: deleteMutation.mutateAsync,
    isSaving: upsertMutation.isPending,
    isValidating: validateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
